import { gql } from "@apollo/client";
import type { MockedResponse } from "@apollo/client/testing";

export const GET_DASHBOARD = gql`
  query getDashboard(
    $name: SummaryAPIS!
    $selection: [String]
    $group_by: [String]
    $aggregate_functions: [AggregateFunctions]
    $round_functions: [RoundFunctions]
    $filters_config: FiltersConfigInput
    $filter_negatives: Boolean
    $filter_null_groups: Boolean
    $max_results: Int
  ) {
    summary(
      name: $name
      selection: $selection
      group_by: $group_by
      aggregate_functions: $aggregate_functions
      round_functions: $round_functions
      filters_config: $filters_config
      filter_negatives: $filter_negatives
      filter_null_groups: $filter_null_groups
      max_results: $max_results
    )
  }
`;

export const GET_DASHBOARDS = gql`
  query getDashboards {
    dashboards {
      edges {
        node {
          id
          global_id
          name
          is_favorite
          is_default
          is_editable
          visibility
          owner_id
          section {
            id
            name
            kind
            created_by
          }
          creator {
            email
          }
        }
      }
    }
  }
`;

export interface GenerateGetDashboardsMockOptions {
  result?: Record<string, unknown> | null;
}

export const generateGetDashboardsMock = ({
  result = null,
}: GenerateGetDashboardsMockOptions): MockedResponse => {
  let getDashboardsMock: MockedResponse = {
    request: {
      query: GET_DASHBOARDS,
    },
  };

  if (result) {
    return {
      ...getDashboardsMock,
      result: { data: result },
    };
  }

  return { ...getDashboardsMock, result: {} };
};

export const GET_DASHBOARD_SECTIONS = gql`
  query getDashboardSections {
    dashboard_sections {
      edges {
        node {
          id
          name
          kind
        }
      }
    }
  }
`;

export interface GenerateGetDashboardSectionsMockOptions {
  result?: Record<string, unknown> | null;
}

export const generateGetDashboardSectionsMock = ({
  result = null,
}: GenerateGetDashboardSectionsMockOptions): MockedResponse => {
  const getDashboardSectionsMock: MockedResponse = {
    request: {
      query: GET_DASHBOARD_SECTIONS,
    },
  };

  if (result) {
    return {
      ...getDashboardSectionsMock,
      result: { data: result },
    };
  }

  return { ...getDashboardSectionsMock, result: {} };
};

export const MOVE_DASHBOARDS = gql`
  mutation MoveDashboards($input: MoveDashboardsInput!) {
    move_dashboards(input: $input) {
      section {
        id
        name
        kind
      }
    }
  }
`;

export const GET_WIDGETS_DEFINITIONS = gql`
  query GetWidgetDefinitions {
    widget_definitions {
      edges {
        node {
          min_height
          min_width
          max_width
          default_name
          store_name
          has_time_controller
          widget_description
          vertical_legend
          chart_type
          category
          id
          key
        }
      }
    }
  }
`;

export const GET_DASHBOARD_DATA = gql`
  query getDashboards($id: Int) {
    dashboards(id: $id) {
      edges {
        node {
          id
          global_id
          name
          creation_date
          creator {
            first_name
            last_name
            email
          }
          is_favorite
          is_editable
          is_default
          visibility
          owner_id
          section {
            id
            name
            kind
          }
          widgets {
            edges {
              node {
                id
                x
                y
                width
                height
                name
                selection_rule {
                  filter {
                    id
                    name
                    filter_data
                    system
                  }
                  scope {
                    filter_data
                  }
                  scopesid
                  scope_group
                }
                widget_definition {
                  key
                  min_height
                  min_width
                  max_width
                  default_name
                  has_time_controller
                  widget_description
                  widget_help_description
                  vertical_legend
                  is_clickable
                  chart_type
                  id
                }
                custom_widget {
                  id
                  default_name
                  query_definition
                  description
                  created_by
                  chart_type {
                    id
                    min_height
                    min_width
                    max_width
                    has_time_controller
                    vertical_legend
                    is_clickable
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_WIDGET_CATEGORIES = gql`
  query GetWidgetCategoriesDefinitions {
    widgets_categories_definitions {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_DASHBOARD_WIDGETS = gql`
  mutation UpdateDashboardWidgets($id: Int!, $widgets: [WidgetInput!]!) {
    update_widgets(id: $id, widgets: $widgets) {
      ok
      msg
    }
  }
`;

export const CREATE_DASHBOARD = gql`
  mutation CreateDashboard($input: CreateDashboardInput!) {
    create_dashboard(input: $input) {
      dashboard_id
    }
  }
`;

export const DUPLICATE_DASHBOARD = gql`
  mutation DuplicateDashboard($id: Int!, $input: DuplicateDashboardInput) {
    duplicate_dashboard(id: $id, input: $input) {
      new_dashboard_id
    }
  }
`;

export const UPDATE_DASHBOARD = gql`
  mutation UpdateDashboard($id: Int!, $dashboard: DashboardInput!) {
    update_dashboard(id: $id, dashboard: $dashboard) {
      ok
      msg
    }
  }
`;

export const SET_DEFAULT_DASHBOARD = gql`
  mutation SetDefaultDashboard($id: Int!) {
    set_default_dashboard(id: $id) {
      ok
      msg
    }
  }
`;

export const SET_DASHBOARD_FAVORITE = gql`
  mutation SetDashboardFavorite($id: Int!, $is_favorite: Boolean!) {
    set_dashboard_favorite(id: $id, is_favorite: $is_favorite) {
      ok
      msg
    }
  }
`;

export const SHARE_DASHBOARD = gql`
  mutation ShareDashboard($id: Int!) {
    share_dashboard(id: $id) {
      dashboard {
        id
        global_id
        name
        is_favorite
        is_default
        is_editable
        visibility
        owner_id
        section {
          id
          name
          kind
        }
        creator {
          email
        }
      }
    }
  }
`;

export const EDIT_DASHBOARD_SECTION = gql`
  mutation EditDashboardSection($id: Int!, $name: String!) {
    edit_dashboard_section(id: $id, name: $name) {
      section {
        id
      }
    }
  }
`;

export const DELETE_DASHBOARDS = gql`
  mutation DeleteDashboards($dashboard_ids: [Int!]!) {
    delete_dashboards(dashboard_ids: $dashboard_ids) {
      ok
      msg
    }
  }
`;
